import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import FileSelectWithPreview from './FileSelectWithPreview';
import ExampleFilePreview from './ExampleFilePreview';
import SkuReorderPlan from './SkuReorderPlan';
import { skuForecast, demandOnDay, next12Months } from './skuForecast';
import {
  parseDate, daysBetween, withDateOffsets, restockEventsBySku,
  groupSummary, buildUrgencyFigure, buildGrowthTrendFigure, GROWTH_SOURCE_LABEL,
} from './skuFamilyLiveEngine';
import './SkuFamilyLiveInvPage.css';

const URGENCY_COLOR = { Critical: '#f87171', High: '#fb923c', Medium: '#fbbf24', Low: '#4ade80', Unknown: '#475569' };
const PLOTLY_SRC = 'https://cdn.plot.ly/plotly-2.35.2.min.js';

// SKU prefixes we don't restock (for now): kept out of the reorder plan, but
// shown on the runout charts while in stock (hit list / what to promote).
// Art mats: ETB-ART-CS (Chris Seaman), ETB-ART-PS (Phil Stone), etc. — all
// shown together as one group; out-of-stock ones are dropped entirely.
const NOT_RESTOCKED_PREFIXES = ['ETB-ART-'];
const NOT_RESTOCKED_GROUP = 'Art Mats (not restocked)';
const isNotRestocked = s => NOT_RESTOCKED_PREFIXES.some(p => s.sku.startsWith(p));

// All class names in this file are prefixed `sfl-` and defined in
// SkuFamilyLiveInvPage.css only.

function loadPlotly() {
  if (window.Plotly) return Promise.resolve(window.Plotly);
  if (!window.__sflPlotlyPromise) {
    window.__sflPlotlyPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PLOTLY_SRC;
      script.async = true;
      script.onload = () => resolve(window.Plotly);
      script.onerror = () => reject(new Error('Failed to load Plotly.js from CDN'));
      document.head.appendChild(script);
    });
  }
  return window.__sflPlotlyPromise;
}

// ── small helpers ──────────────────────────────────────────────────────────────

function ModeToggle({ value, onChange, options }) {
  return (
    <div className="sfl-toggle">
      {options.map(o => (
        <button key={o.value} type="button"
          className={value === o.value ? 'active' : ''}
          onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const fmtGrowth = r => (r == null ? '—' : `${Math.round(r * 100) >= 0 ? '+' : ''}${Math.round(r * 100) || 0}%`);
const fmtUnits = v => (v == null ? '—' : Math.round(v).toLocaleString());
const fmtPerDay = v => (v == null ? '—' : v.toFixed(v < 10 ? 2 : 1));

const sourceText = ch => GROWTH_SOURCE_LABEL[ch.source] + (ch.source_key || ch.sourceKey ? ` (${ch.source_key || ch.sourceKey})` : '');

// Per-family forecast, by channel: D2C, POS and B2B (DIST/WHSL) each get
// units per day on sale this year vs last, their own growth rate and demand
// today — the projection adds them. See sku_family_growth.py's _yoy_growth.
function GrowthRateControl({ label, groupBy, growthEntry, growthData, growthDataError, stockHistoryThrough }) {
  const y = growthEntry?.yoy;
  const fc = y?.forecast;
  const isFamilyMode = groupBy === 'family';

  return (
    <div className="sfl-control-block sfl-forecast-block">
      <label className="sfl-label">
        Family forecast — <span className="sfl-current-group">{label}</span>
      </label>

      {fc && (
        <table className="sfl-channel-growth">
          <thead>
            <tr><th></th><th>Now /day</th><th>Growth</th><th>Next 12 mo</th></tr>
          </thead>
          <tbody>
            {[['d2c', 'D2C'], ['pos', 'POS'], ['wholesale', 'B2B']].map(([c, name]) => (
              <tr key={c} title={`${sourceText(fc.channels[c])} · this yr ${fmtPerDay(fc.channels[c].this_year_per_day)}/day`}>
                <td>{name}</td>
                <td>{fmtPerDay(fc.channels[c].today_per_day)}</td>
                <td className={fc.channels[c].source === 'family' ? '' : 'sfl-dim'}><strong>{fmtGrowth(fc.channels[c].rate)}</strong></td>
                <td>{fmtUnits(fc.channels[c].expected_next_12mo)}</td>
              </tr>
            ))}
            <tr>
              <td>Total</td><td><strong>{fmtPerDay(fc.today_per_day)}</strong></td>
              <td><strong>{fmtGrowth(fc.rate)}</strong></td><td><strong>{fmtUnits(fc.expected_next_12mo)}</strong></td>
            </tr>
          </tbody>
        </table>
      )}
      {fc && (
        <p className="sfl-hint">
          All channels: {fmtPerDay(y.last_year_per_day)}/day ({y.last_year[0]}–{y.last_year[1]}) →{' '}
          {fmtPerDay(y.this_year_per_day)}/day ({y.this_year[0]}–{y.this_year[1]}). Each channel's growth is pooled
          from the family's qualifying SKUs (dimmed = from the category instead) — hover a row.
        </p>
      )}

      {!isFamilyMode && (
        <p className="sfl-hint">Family rates are shown in SKU Family mode — each SKU still uses its own rates below.</p>
      )}
      {isFamilyMode && growthDataError && <p className="sfl-hint">Computed rates unavailable: {growthDataError}</p>}
      {isFamilyMode && growthData && !y && (
        <p className="sfl-hint">No order history for this family.</p>
      )}
      {growthData && !stockHistoryThrough && (
        <p className="sfl-hint">No stock history cached yet — this year's days aren't cut to days in stock. Run a Live from Shopify load to fetch it.</p>
      )}
    </div>
  );
}

// Per-SKU forecast actually applied to the selected group's runout bars, by
// channel. A dimmed growth rate was inherited from the family or category —
// hover for where from.
function SkuGrowthTable({ groupSkus, labelField, skuGrowthData, growthData }) {
  const rows = groupSkus.map(s => {
    const entry = skuGrowthData ? skuGrowthData[s.sku] : null;
    const fc = skuForecast(s, entry, growthData ? growthData[s.family] : null);
    return { sku: s.sku, label: s[labelField] || s.sku, own: entry?.yoy?.forecast, fc, next12: next12Months(fc) };
  }).sort((a, b) => (b.fc.rate ?? -9) - (a.fc.rate ?? -9));

  const channelCells = (r, key, server) => {
    const ch = r.fc[key];
    const own = r.own?.channels[server];
    return (
      <>
        <td title={own?.last_year_per_day != null
          ? `${fmtPerDay(own.last_year_per_day)}/day last yr → ${fmtPerDay(own.this_year_per_day)}/day this yr` : ''}>
          {fmtPerDay(ch.today)}
        </td>
        <td className={ch.source === 'sku' ? '' : 'sfl-dim'} title={sourceText(ch)}>{fmtGrowth(ch.rate)}</td>
      </>
    );
  };

  return (
    <div className="sfl-sku-growth">
      <h3 className="sfl-h3">Forecast by SKU — D2C, POS and B2B</h3>
      <table className="sfl-sku-growth-table">
        <thead>
          <tr>
            <th>SKU</th><th></th>
            <th title="D2C (web store) demand per day today — hover for last yr → this yr">D2C /day</th>
            <th title="D2C growth, units per day year over year">D2C gr.</th>
            <th title="POS (convention booth) demand per day today — hover for last yr → this yr">POS /day</th>
            <th title="POS growth, units per day year over year">POS gr.</th>
            <th title="B2B (distributor + wholesale) demand per day today — hover for last yr → this yr">B2B /day</th>
            <th title="B2B growth, units per day year over year">B2B gr.</th>
            <th title="D2C + POS + B2B demand per day today">Total /day</th>
            <th title="Change in total daily demand over the next year">Growth</th>
            <th title="Units over the next 12 months on the forecast curve">Next 12 mo</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.sku}>
              <td className="sfl-mono">{r.sku}</td>
              <td>{r.label}</td>
              {channelCells(r, 'd2c', 'd2c')}
              {channelCells(r, 'pos', 'pos')}
              {channelCells(r, 'b2b', 'wholesale')}
              <td title={r.fc.basis}><strong>{fmtPerDay(r.fc.today)}</strong></td>
              <td><strong>{fmtGrowth(r.fc.rate)}</strong></td>
              <td>{fmtUnits(r.next12)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sfl-hint">
        Each channel: units per day on sale this year vs last (this year: per day in stock), first 90 days after
        launch left out. Its 12-month rate is brought forward to today and grown at its own rate for 12 months, then
        flat; the run-out bars use D2C + POS + B2B day by day. Dimmed growth is inherited from the family or category —
        hover for which.
      </p>
    </div>
  );
}

function FilePickButton({ file, setFile, inputRef, accept, placeholder = 'Choose file…' }) {
  return (
    <div className="sfl-file-pick">
      <button type="button" className="sfl-pick-btn" onClick={() => inputRef.current.click()}>
        {file ? file.name : placeholder}
      </button>
      <input ref={inputRef} type="file" accept={accept} hidden
        onChange={e => setFile(e.target.files[0] || null)} />
      {file && (
        <button type="button" className="sfl-clear-btn"
          onClick={() => { setFile(null); inputRef.current.value = ''; }}>✕</button>
      )}
    </div>
  );
}

// Restock plan picker + upload. A plan can be a csv/xlsx/xls file or a Google
// Sheet link; the server parses it, saves it to the library, and hands back its
// events so it's selectable right away without reloading the inventory data.
function RestockPlanPicker({ names, value, onChange, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const fileRef = useRef();

  async function upload() {
    const fd = new FormData();
    if (file) fd.append('file', file);
    else fd.append('sheet_url', sheetUrl.trim());
    setBusy(true); setError(null); setNotice(null);
    try {
      const { data } = await axios.post('/api/inventory-model/restock-plans', fd);
      onUploaded(data.name, data.events);
      setNotice(`Loaded ${data.name}: ${data.events.length} rows`
        + (data.skipped ? ` (${data.skipped} skipped: missing SKU, date or qty)` : ''));
      close();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setOpen(false); setError(null); setFile(null); setSheetUrl('');
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <div className="sfl-plan-picker">
      <div className="sfl-plan-row">
        <select className="sfl-select" value={value} onChange={e => onChange(e.target.value)}>
          <option value="">None</option>
          {names.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        <button type="button" className="sfl-pick-btn" onClick={() => { setOpen(true); setNotice(null); }}>
          Upload plan
        </button>
      </div>
      {notice && <p className="sfl-hint">{notice}</p>}

      {open && (
        <div className="sfl-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) close(); }}>
          <div className="sfl-modal" role="dialog" aria-label="Upload restock plan">
            <h3 className="sfl-modal-title">Upload restock plan</h3>
            <p className="sfl-hint">
              Columns: <strong>SKU, Restock_Date, Restock_Qty</strong> (PO_Number and Notes optional).
            </p>

            <label className="sfl-label">CSV or Excel file</label>
            <FilePickButton file={file} setFile={f => { setFile(f); if (f) setSheetUrl(''); }}
              inputRef={fileRef} accept=".csv,.xlsx,.xls" placeholder="Choose file…" />
            <p className="sfl-hint">Excel files use the first tab.</p>

            <div className="sfl-modal-or"><span>or</span></div>

            <label className="sfl-label">Google Sheet link</label>
            <input type="url" className="sfl-input" placeholder="https://docs.google.com/spreadsheets/d/…"
              value={sheetUrl} disabled={!!file} onChange={e => setSheetUrl(e.target.value)} />
            <p className="sfl-hint">Uses the tab the link points to. The sheet must be shared with the tools' Google
              account, or viewable by anyone with the link.</p>

            {error && <div className="sfl-error sfl-modal-error">{error}</div>}

            <div className="sfl-modal-actions">
              <button type="button" className="sfl-btn-secondary" onClick={close} disabled={busy}>Cancel</button>
              <button type="button" className="sfl-btn-primary" onClick={upload}
                disabled={busy || (!file && !sheetUrl.trim())}>
                {busy ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── phase 1: pick the inventory report (fixed for the rest of the session) ───

function LoadForm({ onLoaded }) {
  const [invReports, setInvReports] = useState([]);
  const [invMode, setInvMode] = useState('live'); // 'live' | 'saved' | 'inv-mgmt' | 'upload'
  const [selectedInvReport, setSelectedInvReport] = useState('');
  const [invFile, setInvFile] = useState(null);
  const [invMgmtReports, setInvMgmtReports] = useState([]);
  const [selectedInvMgmtReport, setSelectedInvMgmtReport] = useState('');
  const [markets, setMarkets] = useState([]);
  const [market, setMarket] = useState(''); // '' = Shipping Pilot stock, all sales (original behavior)
  const invRef = useRef();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [liveJobId, setLiveJobId] = useState(null);
  const [liveLogs, setLiveLogs] = useState([]);

  useEffect(() => {
    Promise.allSettled([
      axios.get('/api/inventory-model/inventory-reports'),
      axios.get('/api/inventory-mgmt/results'),
      axios.get('/api/sku-family-live/markets'),
    ]).then(([savedRes, mgmtRes, marketsRes]) => {
      const saved = savedRes.status === 'fulfilled' ? savedRes.value.data : [];
      const mgmt  = mgmtRes.status === 'fulfilled' ? mgmtRes.value.data : [];
      setMarkets(marketsRes.status === 'fulfilled' ? marketsRes.value.data : []);
      setInvReports(saved);
      setInvMgmtReports(mgmt);
      if (saved.length > 0) setSelectedInvReport(saved[saved.length - 1].name);
      if (mgmt.length > 0) setSelectedInvMgmtReport(mgmt[0].name);
    });
  }, []);

  // Live from Shopify runs as a background job (the 180-day sales pull takes
  // a couple of minutes) — poll it until it hands back the payload.
  useEffect(() => {
    if (!liveJobId) return;
    const iv = setInterval(() => {
      axios.get(`/api/sku-family-live/logs/${liveJobId}`)
        .then(r => {
          setLiveLogs(r.data.lines);
          if (r.data.status === 'complete' && r.data.result) {
            setLiveJobId(null); setLoading(false); onLoaded(r.data.result);
          } else if (r.data.status === 'error') {
            setLiveJobId(null); setLoading(false); setError(r.data.error || 'Live load failed');
          }
        })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(iv);
  }, [liveJobId, onLoaded]);

  function handleSubmit(e) {
    e.preventDefault();
    if (invMode === 'upload' && !invFile) return;
    if (invMode === 'saved' && !selectedInvReport) return;
    if (invMode === 'inv-mgmt' && !selectedInvMgmtReport) return;
    setLoading(true); setError(null);

    if (invMode === 'live') {
      setLiveLogs([]);
      axios.post('/api/sku-family-live/live-run', market ? { market } : {})
        .then(r => setLiveJobId(r.data.job_id))
        .catch(e => { setError(e.response?.data?.error || e.message); setLoading(false); });
      return;
    }

    const fd = new FormData();
    if (invMode === 'upload' && invFile) {
      fd.append('inventory', invFile);
    } else if (invMode === 'inv-mgmt') {
      fd.append('inv_mgmt_report_name', selectedInvMgmtReport);
    } else {
      fd.append('inventory_report_name', selectedInvReport);
    }

    axios.post('/api/sku-family-live/load', fd)
      .then(r => { setLoading(false); onLoaded(r.data); })
      .catch(e => { setError(e.response?.data?.error || e.message); setLoading(false); });
  }

  return (
    <section className="sfl-section">
      <h1>SKU Family Inventory — Live</h1>
      <p className="sfl-muted">
        Pull current stock and sales live from Shopify, or pick a saved inventory report — this is
        the one thing that stays fixed for the session.
        Once loaded, growth rate, target dates, restock plan, and family/color grouping are all
        adjustable live, with the chart updating instantly.
      </p>

      {error && <div className="sfl-error">{error}</div>}

      <form className="sfl-form" onSubmit={handleSubmit}>
        <div className="sfl-field">
          <label className="sfl-label">Inventory Data <span className="sfl-req">*</span></label>
          <ModeToggle value={invMode} onChange={setInvMode} options={[
            { value: 'live', label: 'Live from Shopify' },
            { value: 'saved', label: 'Saved report' },
            { value: 'inv-mgmt', label: 'From Inventory Mgmt' },
            { value: 'upload', label: 'Upload new' },
          ]} />
          {invMode === 'live' && (
            <>
              {markets.length > 0 && (
                <select className="sfl-select" value={market} onChange={e => setMarket(e.target.value)}>
                  <option value="">Shipping Pilot — all sales (default)</option>
                  {markets.map(m => (
                    <option key={m.key} value={m.key}>{m.label} — {m.locations.join(' + ') || 'no location'}</option>
                  ))}
                </select>
              )}
              <p className="sfl-hint">
                {market
                  ? <>Market view: stock summed across this market's Shopify locations, and only this
                    market's last-180-day sales (orders shipped to its countries, or sold at / fulfilled
                    from its locations; the US takes every order no other market claims). Growth rates
                    are still company-wide. Markets are configured in inventory_app/sku_markets.json.</>
                  : <>Fetches current Shipping Pilot stock and the last 180 days of sales straight from
                    Shopify (same tracked SKUs and sell-rate math as Inventory Management).</>}
                {' '}Takes about 2–3 minutes; nothing is saved.
              </p>
            </>
          )}
          {invMode === 'saved' && (
            invReports.length === 0 ? (
              <p className="sfl-hint">No saved reports yet — upload one first.</p>
            ) : (
              <FileSelectWithPreview
                items={invReports}
                value={selectedInvReport}
                onChange={setSelectedInvReport}
                renderLabel={r => `${r.name} (${r.size_kb} KB)`}
                previewUrl={r => `/api/inventory-model/inventory-reports/${encodeURIComponent(r.name)}/preview`}
                placeholder="Select a saved report…"
              />
            )
          )}
          {invMode === 'inv-mgmt' && (
            invMgmtReports.length === 0 ? (
              <p className="sfl-hint">No Inventory Management reports yet — run that tool first.</p>
            ) : (
              <FileSelectWithPreview
                items={invMgmtReports}
                value={selectedInvMgmtReport}
                onChange={setSelectedInvMgmtReport}
                renderLabel={r => `${r.as_of || r.name}${r.total_skus ? ` — ${r.total_skus} SKUs` : ''}`}
                previewUrl={r => `/api/inventory-mgmt/results/${encodeURIComponent(r.name)}/preview`}
                placeholder="Select a report…"
              />
            )
          )}
          {invMode === 'upload' && (
            <FilePickButton file={invFile} setFile={setInvFile} inputRef={invRef} accept=".xlsx,.xls,.csv" />
          )}
          {(invMode === 'saved' || invMode === 'upload') && (
            <ExampleFilePreview
              label="View example format"
              previewUrl="/api/inventory-model/examples/inventory-report/preview"
            />
          )}
        </div>

        <p className="sfl-hint">
          Restock plans are picked up automatically from the saved library and switchable live —
          there's no need to choose one here. Upload a new one (CSV, Excel or Google Sheet link)
          from the Restock plan picker once the data is loaded.
        </p>

        <button type="submit" className="sfl-btn-primary"
          disabled={loading || (invMode === 'upload' && !invFile) || (invMode === 'saved' && !selectedInvReport)}>
          {loading ? (invMode === 'live' ? 'Fetching from Shopify…' : 'Loading…') : 'Load →'}
        </button>
      </form>

      {invMode === 'live' && liveLogs.length > 0 && (
        <div className="sfl-live-log">
          {liveLogs.map((line, i) => <div key={i}>{line}</div>)}
        </div>
      )}
    </section>
  );
}

// ── phase 2: the live workspace ───────────────────────────────────────────────

function LiveWorkspace({ payload, onReset }) {
  const asOf = useMemo(() => parseDate(payload.as_of), [payload.as_of]);
  // familyGroup = the charts' "SKU Family" grouping; `family` itself stays the
  // real family so growth lookups (growthData[s.family]) still work.
  const allSkus = useMemo(() => withDateOffsets(
    payload.skus
      .filter(s => !isNotRestocked(s) || s.stock > 0)
      .map(s => ({ ...s, familyGroup: isNotRestocked(s) ? NOT_RESTOCKED_GROUP : s.family })),
    asOf,
  ), [payload.skus, asOf]);
  const skus = useMemo(() => allSkus.filter(s => !isNotRestocked(s)), [allSkus]);

  // Starts as the saved library; plans uploaded from this page are added in.
  const [restockPlans, setRestockPlans] = useState(payload.restock_plans || {});
  const restockPlanNames = useMemo(() => Object.keys(restockPlans), [restockPlans]);

  const [view, setView] = useState('charts'); // 'charts' | 'reorder'
  const [groupBy, setGroupBy] = useState('family'); // 'family' | 'color'
  // growthData/growthDataError come straight off the loaded payload — computed
  // server-side as part of /api/sku-family-live/load (see sku_family_live_load),
  // fresh for every report load, not fetched separately.
  const growthData = payload.growth_rates || null;      // { [family]: {window_12mo, ...} } | null
  const skuGrowthData = payload.sku_growth_rates || null; // { [sku]: {window_12mo, units_12mo, low_volume, ...} } | null
  const growthDataError = payload.growth_rates_error || null;
  const [bufferDays, setBufferDays] = useState(30);
  const [globalTargetDate, setGlobalTargetDate] = useState('');
  const [targetOverrides, setTargetOverrides] = useState({});
  const [restockPlanName, setRestockPlanName] = useState(restockPlanNames[0] || '');
  const [selectedGroup, setSelectedGroup] = useState(null);

  const groupField = groupBy === 'family' ? 'familyGroup' : 'color';
  const labelField = groupBy === 'family' ? 'color' : 'family';

  const skusByGroup = useMemo(() => {
    const m = new Map();
    for (const s of allSkus) {
      const key = s[groupField];
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(s);
    }
    return m;
  }, [allSkus, groupField]);

  const summaries = useMemo(() => {
    const rows = Array.from(skusByGroup.entries()).map(([label, groupSkus]) => groupSummary(groupSkus, label, asOf));
    rows.sort((a, b) => {
      const ax = a.daysToFirst == null ? Infinity : a.daysToFirst;
      const bx = b.daysToFirst == null ? Infinity : b.daysToFirst;
      return ax - bx;
    });
    return rows;
  }, [skusByGroup, asOf]);

  // Reset selection (and per-group overrides, which don't carry meaning across
  // groupings) whenever we switch between family and color analysis.
  useEffect(() => {
    setSelectedGroup(summaries.length > 0 ? summaries[0].label : null);
    setTargetOverrides({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  const restockBySku = useMemo(() => {
    if (!restockPlanName || !restockPlans[restockPlanName]) return new Map();
    return restockEventsBySku(restockPlans[restockPlanName], asOf);
  }, [restockPlanName, restockPlans, asOf]);

  const addRestockPlan = (name, events) => {
    setRestockPlans(prev => ({ ...prev, [name]: events }));
    setRestockPlanName(name);
  };
  const planPicker = (
    <RestockPlanPicker names={restockPlanNames} value={restockPlanName}
      onChange={setRestockPlanName} onUploaded={addRestockPlan} />
  );

  const growthEntryFor = g => (groupBy === 'family' && growthData) ? growthData[g] : null;

  const currentGrowthEntry = selectedGroup ? growthEntryFor(selectedGroup) : null;

  const targetDateStrFor = g => targetOverrides[g] || globalTargetDate;

  const figure = useMemo(() => {
    if (!selectedGroup) return null;
    const groupSkus = skusByGroup.get(selectedGroup) || [];
    const targetStr = targetDateStrFor(selectedGroup);
    const targetDateOffset = targetStr ? daysBetween(asOf, parseDate(targetStr)) : null;
    const forecastForSku = s => {
      const fc = skuForecast(s, skuGrowthData ? skuGrowthData[s.sku] : null, growthData ? growthData[s.family] : null);
      return { fc, demandOn: i => demandOnDay(fc, i) };
    };
    return buildUrgencyFigure({
      groupSkus, labelField, groupTitle: selectedGroup, asOf,
      forecastForSku, bufferDays, targetDateOffset, restockBySku,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup, skusByGroup, labelField, asOf, skuGrowthData, growthData, bufferDays, restockBySku, globalTargetDate, targetOverrides]);

  const growthChartRef = useRef(null);
  const growthFigure = useMemo(() => {
    if (!selectedGroup || !currentGrowthEntry) return null;
    return buildGrowthTrendFigure(currentGrowthEntry, selectedGroup);
  }, [selectedGroup, currentGrowthEntry]);

  const chartRef = useRef(null);
  const [plotlyReady, setPlotlyReady] = useState(!!window.Plotly);
  const [plotlyError, setPlotlyError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadPlotly().then(() => { if (!cancelled) setPlotlyReady(true); })
      .catch(e => { if (!cancelled) setPlotlyError(e.message); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!plotlyReady || !chartRef.current) return;
    if (!figure) {
      window.Plotly.purge(chartRef.current);
      return;
    }
    window.Plotly.react(chartRef.current, figure.data, figure.layout, { responsive: true, displaylogo: false });
  }, [plotlyReady, figure]);

  useEffect(() => {
    if (!plotlyReady || !growthChartRef.current) return;
    if (!growthFigure) {
      window.Plotly.purge(growthChartRef.current);
      return;
    }
    window.Plotly.react(growthChartRef.current, growthFigure.data, growthFigure.layout, { responsive: true, displaylogo: false });
  }, [plotlyReady, growthFigure]);

  // The control bar can reflow, so the charts' width can change without a
  // window resize — Plotly's `responsive` only listens to the window.
  useEffect(() => {
    if (!plotlyReady || typeof ResizeObserver === 'undefined') return;
    const els = [chartRef.current, growthChartRef.current].filter(Boolean);
    const ro = new ResizeObserver(entries => {
      entries.forEach(e => { if (e.target.data) window.Plotly.Plots.resize(e.target); });
    });
    els.forEach(el => ro.observe(el));
    return () => ro.disconnect();
  }, [plotlyReady, figure, growthFigure]);

  const currentSummary = summaries.find(r => r.label === selectedGroup);

  return (
    <div className="sfl-workspace">
      <div className="sfl-workspace-header">
        <div>
          <h1>SKU Family Inventory — Live</h1>
          <p className="sfl-muted">
            As of <strong>{payload.as_of}</strong>{payload.source === 'live' ? ' (live from Shopify)' : ''} · {summaries.length} {groupBy === 'family' ? 'families' : 'colors'}
            {payload.market && <> · <strong>{payload.market.label}</strong> ({payload.market.locations.join(' + ')})</>}
          </p>
          {payload.market?.missing_locations?.length > 0 && (
            <div className="sfl-error">
              Not found in Shopify, skipped: {payload.market.missing_locations.join(', ')}
            </div>
          )}
        </div>
        <button className="sfl-btn-secondary" onClick={onReset}>Change inventory data</button>
      </div>

      <div className="sfl-view-bar">
        <ModeToggle value={view} onChange={setView} options={[
          { value: 'charts', label: 'Runout charts' },
          { value: 'reorder', label: 'Reorder plan' },
        ]} />
        {view === 'reorder' && (
          <div className="sfl-view-bar-plan">Restock plan {planPicker}</div>
        )}
      </div>

      {view === 'reorder' && (
        <SkuReorderPlan
          skus={skus} asOf={asOf} asOfStr={payload.as_of}
          skuGrowthData={skuGrowthData} growthData={growthData}
          restockEvents={restockPlanName ? restockPlans[restockPlanName] : []}
          restockPlanName={restockPlanName}
        />
      )}

      {view === 'charts' && <>
      <div className="sfl-layout">
        <aside className="sfl-controls">
          <div className="sfl-control-block">
            <label className="sfl-label">Analyze by</label>
            <ModeToggle value={groupBy} onChange={setGroupBy} options={[
              { value: 'family', label: 'SKU Family' },
              { value: 'color', label: 'Color' },
            ]} />
          </div>

          {currentSummary && (
            <GrowthRateControl
              label={selectedGroup}
              groupBy={groupBy}
              growthEntry={currentGrowthEntry}
              growthData={growthData}
              growthDataError={growthDataError}
              stockHistoryThrough={payload.stock_history_through}
            />
          )}

          <div className="sfl-control-block">
            <label className="sfl-label">Target buffer (days)</label>
            <input type="number" className="sfl-num" value={bufferDays}
              onChange={e => setBufferDays(parseInt(e.target.value, 10) || 0)} />
          </div>

          <div className="sfl-control-block">
            <label className="sfl-label">Global target date</label>
            <input type="date" className="sfl-date" style={{ colorScheme: 'dark' }}
              value={globalTargetDate}
              onChange={e => setGlobalTargetDate(e.target.value)} />
          </div>

          <div className="sfl-control-block">
            <label className="sfl-label">Restock plan</label>
            {planPicker}
          </div>

          {currentSummary && (
            <div className="sfl-control-block">
              <label className="sfl-label">
                Target date override — <span className="sfl-current-group">{selectedGroup}</span>
              </label>
              <input type="date" className="sfl-date" style={{ colorScheme: 'dark' }}
                value={targetOverrides[selectedGroup] || ''}
                onChange={e => setTargetOverrides(prev => ({ ...prev, [selectedGroup]: e.target.value }))} />
              <p className="sfl-hint">
                {targetOverrides[selectedGroup]
                  ? 'override active'
                  : globalTargetDate ? `← using global: ${globalTargetDate}` : 'no target set'}
              </p>
            </div>
          )}
        </aside>

        <main className="sfl-main">
          {plotlyError && <div className="sfl-error">{plotlyError}</div>}
          {!plotlyReady && !plotlyError && <p className="sfl-muted">Loading chart engine…</p>}
          {plotlyReady && !figure && (
            <p className="sfl-muted">No chartable data for “{selectedGroup}” — no SKU in this group has a usable runout date.</p>
          )}
          {/* Explicit height: Plots.resize (ResizeObserver above) autosizes to the
              container, so without this the chart collapses to min-height. */}
          <div ref={chartRef} className="sfl-chart"
               style={{ display: figure ? 'block' : 'none', height: figure ? figure.layout.height : undefined }} />

          {groupBy === 'family' && (
            <div className="sfl-growth-chart-wrap">
              {plotlyReady && !growthFigure && growthData && (
                <p className="sfl-muted">No order history to chart for “{selectedGroup}”.</p>
              )}
              <div ref={growthChartRef} className="sfl-growth-chart"
                   style={{ display: growthFigure ? 'block' : 'none', height: growthFigure ? growthFigure.layout.height : undefined }} />
            </div>
          )}

          {selectedGroup && (
            <SkuGrowthTable
              groupSkus={skusByGroup.get(selectedGroup) || []}
              labelField={labelField}
              skuGrowthData={skuGrowthData}
              growthData={growthData}
            />
          )}
        </main>
      </div>

      <section className="sfl-section">
        <h2 className="sfl-h2">{groupBy === 'family' ? 'Families' : 'Colors'} — sorted by urgency</h2>
        <div className="sfl-grid">
          {summaries.map(row => (
            <button key={row.label}
              className={`sfl-card${selectedGroup === row.label ? ' active' : ''}`}
              onClick={() => setSelectedGroup(row.label)}>
              <div className="sfl-card-top">
                <span className="sfl-card-name">{row.label}</span>
                <span className="sfl-urgency-badge" style={{ color: URGENCY_COLOR[row.urgency] }}>{row.urgency}</span>
              </div>
              <div className="sfl-card-meta">
                {Math.round(row.totalStock).toLocaleString()} units · {row.skus} SKU{row.skus === 1 ? '' : 's'} ·
                {' '}first runout {row.earliestRunout || 'N/A'}
              </div>
              {targetOverrides[row.label] && (
                <div className="sfl-card-effective">target: {targetOverrides[row.label]}</div>
              )}
            </button>
          ))}
        </div>
      </section>
      </>}
    </div>
  );
}

// ── top-level page ─────────────────────────────────────────────────────────────

export default function SkuFamilyLiveInvPage() {
  const [payload, setPayload] = useState(null);

  return (
    <div className="sfl-page">
      <div className="sfl-breadcrumb">
        <button onClick={() => setPayload(null)} className={payload ? 'link' : ''}>Load report</button>
        <span>›</span>
        <span className={payload ? 'active' : ''}>Live analysis</span>
      </div>

      {!payload
        ? <LoadForm onLoaded={setPayload} />
        : <LiveWorkspace payload={payload} onReset={() => setPayload(null)} />}
    </div>
  );
}
